import React, { FC, useEffect, useRef } from 'react';

import { Severity } from '@ariksa/notification';
import {
  Box,
  Center,
  HStack,
  Input,
  ListItem,
  Stack,
  Text,
  Textarea,
  UnorderedList,
} from '@chakra-ui/react';
import { each, isEmpty, isString, map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { colorOpacity, customTheme } from 'theme';

import {
  DetailsIdentifier,
  getIcon,
  renderExceptionExpiresOn,
  renderRiskContextIcon,
  renderSeverityBarWithCount,
  StackedHeader,
} from 'components/DataDisplay';
import {
  CancelButton,
  PrimaryButton,
  Select,
  SelectOption,
  textAreaFieldStyles,
  textFieldStyles,
} from 'components/DataEntry';
import { IconTypes } from 'components/Icons';
import { Drawer } from 'components/Overlay';
import { errorToast, successToast } from 'components/Toast';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useEnvironmentOptions } from 'containers/App/hooks/useEnvironmentOptions';
import { exceptionApplyTimeOptions } from 'containers/Findings/Alerts/Components/utils';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';

interface Props {
  isOpen: boolean;
  onClose();
  selectedPolicies: Record<string, any>[];
  blueprintId: string;
  onSuccess();
  onError?();
  totalPolicies: number;
}

export const ExcludePoliciesForResource: FC<Props> = props => {
  const {
    isOpen,
    onClose,
    selectedPolicies,
    blueprintId,
    onSuccess,
    totalPolicies,
  } = props;
  const {
    resourceInsight,
    resourceAccountId,
    riskContext,
    alerts,
    resourceUuid,
    resourceType,
    alertAction,
  } = useSelector(selectActiveResource);
  const { environmentId } = useAccessBoundary();
  const { environmentMapping } = useEnvironmentOptions();
  const { accountMapping } = useCloudAccountId();
  const ref = useRef(document.querySelector('.portal-container'));
  const [severities, setSeverities] = React.useState<Record<string, number>>(
    {},
  );
  const [confirm, setConfirm] = React.useState(false);
  const [
    exceptionApplyTime,
    setExceptionApplyTime,
  ] = React.useState<SelectOption>(exceptionApplyTimeOptions[0]);
  const dispatch = useDispatch();
  const [exceptionDescription, setExceptionDescription] = React.useState('');

  useEffect(() => {
    let items = {
      [Severity.Critical]: 0,
      [Severity.High]: 0,
      [Severity.Medium]: 0,
      [Severity.Low]: 0,
    };
    each(selectedPolicies, o => {
      if (o?.severity === Severity.Critical) items[Severity.Critical] += 1;
      else if (o?.severity === Severity.High) items[Severity.High] += 1;
      else if (o?.severity === Severity.Medium) items[Severity.Medium] += 1;
      else if (o?.severity === Severity.Low) items[Severity.Low] += 1;
    });
    setSeverities(items);
  }, [selectedPolicies]);

  const renderIconField = (value, iconType, color) => {
    return (
      <HStack align="center" spacing={1}>
        <Center
          boxSize={6}
          color={color}
          p={0.5}
          border="1px solid"
          borderColor={customTheme.colors.gray['100']}
          bg={colorOpacity(color, 0.1)}
          borderRadius={5}
        >
          <Center>{getIcon(iconType)}</Center>
        </Center>
        <Box>{value}</Box>
      </HStack>
    );
  };

  const handleOnClickOk = () => {
    dispatch(
      actions.addException({
        q: {
          blueprintExceptionCreate: {
            blueprint_ids: [blueprintId],
            environment_id: environmentId!,
            resource_ids: resourceUuid ? [resourceUuid] : undefined,
            rule_ids: map(selectedPolicies, o => o?.policy_id || o?.rule_id),
            native_resource: resourceType,
            description: exceptionDescription,
            expire_at: exceptionApplyTime?.value,
          },
        },
        onSuccess: res => {
          onSuccess();
          if (isString(res)) {
            successToast({ title: res });
          } else {
            successToast({
              title: `Successfully added exception!`,
            });
          }
          onClose();
        },
        onError: error => {
          errorToast({
            title: `Failed to add exception.`,
            description: error.description,
          });
          onClose();
        },
      }),
    );
  };

  return (
    <>
      <Drawer
        styles={{
          content: { maxW: '4xl' },
          body: { overflowY: 'auto', flex: 1 },
          drawer: {
            //portalProps: { containerRef: ref as any },
            blockScrollOnMount: false,
          },
        }}
        isOpen={isOpen}
        onClose={onClose}
        closeButton
        header={
          <StackedHeader
            upper="Exclude Policies For Resource"
            lower={
              selectedPolicies.length +
              (selectedPolicies.length === 1 ? ' Policy' : ' Policies') +
              ' Selected'
            }
            type={IconTypes.Exception}
          />
        }
        body={
          <Box>
            <Stack flex={1} h="full" pt={3} spacing={5} overflow="auto">
              <DetailsIdentifier
                label="Resource"
                value={
                  [
                    resourceInsight.data?.name,
                    resourceInsight.data?.resource_id,
                  ]
                    .filter(Boolean)
                    .join(' / ') || '-'
                }
              />
              <DetailsIdentifier
                label="Deployed In"
                value={
                  <HStack align="flex-start" spacing={1}>
                    {renderIconField(
                      environmentMapping?.[environmentId!]?.name,
                      IconTypes.Environment,
                      customTheme.colors.blue['300'],
                    )}
                    <Box>/</Box>
                    {renderIconField(
                      accountMapping?.[resourceAccountId!]?.name,
                      IconTypes.Account,
                      customTheme.colors.gray['300'],
                    )}
                    {resourceInsight.data?.region && (
                      <>
                        <Box>/</Box>
                        {renderIconField(
                          resourceInsight.data?.region,
                          IconTypes.Region,
                          customTheme.colors.green['300'],
                        )}
                      </>
                    )}
                    {(resourceInsight.data?.vpc ||
                      resourceInsight.data?.vpc_id) && (
                      <>
                        <Box>/</Box>
                        {renderIconField(
                          resourceInsight.data?.vpc ||
                            resourceInsight.data?.vpc_id,
                          IconTypes.Vpc,
                          customTheme.colors.orange['300'],
                        )}
                      </>
                    )}
                  </HStack>
                }
              />
              <DetailsIdentifier
                label="Risk Context"
                align="center"
                isLoading={riskContext.isLoading}
                value={
                  isEmpty(riskContext.data) ? (
                    'None'
                  ) : (
                    <HStack>
                      {map(riskContext.data, (o, index) => (
                        <Box key={index + '-risk-context'}>
                          {renderRiskContextIcon(o)}
                        </Box>
                      ))}
                    </HStack>
                  )
                }
              />
              <DetailsIdentifier
                label="Current Issues"
                value={
                  <HStack>
                    {map(severities, (count, key) =>
                      count
                        ? renderSeverityBarWithCount({ value: key, count })
                        : null,
                    )}
                  </HStack>
                }
              />
              <DetailsIdentifier
                label="Policies Being Excluded / Total Policies"
                value={selectedPolicies?.length + ' / ' + totalPolicies}
              />
              <DetailsIdentifier
                label="Specify Time Interval to Exclude Policies"
                value={
                  <Stack>
                    <Box w={'50%'}>
                      <Select
                        value={exceptionApplyTime}
                        options={exceptionApplyTimeOptions}
                        onChange={opt => {
                          setExceptionApplyTime?.(opt);
                        }}
                        menuPortalTarget={document.body}
                      />
                    </Box>
                    {renderExceptionExpiresOn(exceptionApplyTime?.value)}
                  </Stack>
                }
                direction="column"
              />
              <DetailsIdentifier
                label={
                  <Text display="inline-block">
                    Reason for exception
                    <Text color={'red'} display="inline-block">
                      *
                    </Text>
                  </Text>
                }
                value={
                  <Box w="80%">
                    <Textarea
                      {...textAreaFieldStyles.textArea}
                      placeholder="Required field for why the exception is being applied."
                      onChange={e => {
                        setExceptionDescription?.(e.target.value.trim());
                      }}
                    />
                  </Box>
                }
                direction="column"
              />

              <Stack fontSize="14px">
                <Box color="primary">NOTE:</Box>
                <UnorderedList>
                  <ListItem pl={4}>
                    This exclude enforcement of all relevant policies for this
                    resource regardless of any existing issues
                  </ListItem>
                  <ListItem pl={4}>
                    Policy exclusion will be for the time interval specified.
                    Once it expires, policies will be enforced automatically
                  </ListItem>
                  <ListItem pl={4}>
                    All alerts related to current issues will be marked resolved
                    automatically
                  </ListItem>
                </UnorderedList>
              </Stack>

              <Stack spacing={3} pt={3}>
                <Text fontSize="14px">
                  Type <Text as="b">“CONFIRM”</Text> below to exclude this
                  resource from all applicable policies.
                </Text>
                <Box w="50%">
                  <Input
                    placeholder="Type CONFIRM to proceed"
                    onChange={e => setConfirm(e.target.value === 'CONFIRM')}
                    color={confirm ? 'green' : 'red'}
                    {...textFieldStyles?.input}
                  />
                </Box>
              </Stack>
            </Stack>
          </Box>
        }
        footer={
          <HStack justify="space-between" pt={6} w="full">
            <CancelButton onClick={onClose}>CANCEL</CancelButton>
            <PrimaryButton
              px={6}
              isDisabled={!confirm || !exceptionDescription}
              onClick={handleOnClickOk}
              isLoading={alertAction.isLoading}
            >
              OK
            </PrimaryButton>
          </HStack>
        }
      />
    </>
  );
};
